import { useState } from 'react';
import type { UserInfo } from '../types';

interface UserInfoFormProps {
  onSubmit: (info: UserInfo) => void;
  isAnalyzing?: boolean;
}

export const UserInfoForm = ({ onSubmit, isAnalyzing }: UserInfoFormProps) => {
  const [formData, setFormData] = useState<UserInfo>({
    nome: '',
    email: '',
  });
  const [errors, setErrors] = useState<Partial<UserInfo>>({});

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Partial<UserInfo> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    if (!formData.email.trim() || !validateEmail(formData.email)) {
      newErrors.email = 'E-mail válido é obrigatório';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  const handleChange = (field: keyof UserInfo, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-user-line text-3xl text-[#d80005]"></i>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
          Últimas informações
        </h2>
        <p className="text-slate-600 text-lg">
          Para gerar seu diagnóstico personalizado
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="nome" className="block text-sm font-semibold text-slate-700 mb-2">
            Nome completo *
          </label>
          <input
            type="text"
            id="nome"
            value={formData.nome}
            onChange={(e) => handleChange('nome', e.target.value)}
            className={`w-full px-4 py-3 rounded-xl border-2 text-slate-800 text-base focus:outline-none focus:ring-2 focus:ring-[#d80005] transition-all ${
              errors.nome ? 'border-red-500' : 'border-slate-200'
            }`}
            placeholder="Seu nome"
            disabled={isAnalyzing}
          />
          {errors.nome && <p className="text-red-500 text-sm mt-1">{errors.nome}</p>}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
            E-mail *
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className={`w-full px-4 py-3 rounded-xl border-2 text-slate-800 text-base focus:outline-none focus:ring-2 focus:ring-[#d80005] transition-all ${
              errors.email ? 'border-red-500' : 'border-slate-200'
            }`}
            placeholder="seu@email.com"
            disabled={isAnalyzing}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        <button
          type="submit"
          disabled={isAnalyzing}
          className="w-full px-8 py-4 bg-[#d80005] text-white rounded-xl font-semibold hover:bg-[#b00004] transition-all shadow-lg hover:shadow-xl whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isAnalyzing ? 'Analisando...' : 'Ver meu diagnóstico'}
        </button>
      </form>

      <p className="text-center text-sm text-slate-500 mt-6">
        Seus dados estão seguros e protegidos
      </p>
    </div>
  );
};
